<template>
  <BaseSidebar
    v-model="isSidebarOpen"
    title="Solicitação de excepcionalidade"
    description="Acompanhe o andamento e os detalhes do pedido de excepcionalidade enviado para aprovação."
  >
    <template v-if="stationContext" #contextHeader>
      <StationContextCard v-bind="stationContext" />
    </template>

    <div v-if="loading" class="flex justify-center items-center h-full min-h-[300px]">
      <v-progress-circular indeterminate color="primary" size="64" />
    </div>

    <div v-else-if="!detailedException" class="text-center py-8">
      <p class="text-textSecondary">Não foi possível carregar os detalhes do pedido.</p>
    </div>

    <div v-else class="flex flex-col gap-y-6 text-textPrimary">
      <template v-if="flags.enabledApprovalEngine && detailedException.approvalInstance">
        <ExceptionalityProgressTimeline
          v-if="timelineProps"
          :header-status="timelineProps.headerStatus"
          :title="timelineProps.title"
          :layers="timelineProps.layers"
          :base-event="timelineProps.baseEvent"
          data-testid="request-exceptionality-progress-timeline"
        />

        <p class="text-md font-bold text-left" data-testid="request-summary-title">
          Resumo do pedido
        </p>

        <StatusBadge :status="detailedException.status" data-testid="request-summary-status" />

        <div class="grid grid-cols-2 gap-x-4 gap-y-6">
          <div class="text-left" data-testid="request-summary-date">
            <p class="text-md font-bold">Data da solicitação</p>
            <p class="mt-1 text-base">
              {{ formatDateTimeWithTimezone(detailedException.requestDate) }}
            </p>
          </div>
          <div class="text-left" data-testid="request-summary-requester">
            <p class="text-md font-bold">Solicitante</p>
            <p class="mt-1 text-base">{{ detailedException.requester.name }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-x-4 gap-y-6">
          <div class="text-left" data-testid="request-summary-approval-date">
            <p class="text-md font-bold">Data da aprovação</p>
            <p class="mt-1 text-base">
              {{
                detailedException.approvalDate
                  ? formatDateTimeWithTimezone(detailedException.approvalDate)
                  : 'Pendente'
              }}
            </p>
          </div>
          <div class="text-left" data-testid="request-summary-approvers">
            <p class="text-md font-bold">Aprovadores</p>
            <p class="mt-1 text-base">
              {{ approvedApproversText || 'Nenhum até o momento' }}
            </p>
          </div>
        </div>

        <div
          v-if="pendingApproversText"
          class="text-left"
          data-testid="request-summary-pending-approvers"
        >
          <p class="text-md font-bold">Aprovadores pendentes</p>
          <p class="mt-1 text-base">{{ pendingApproversText }}</p>
        </div>
      </template>

      <div v-else class="grid grid-cols-2 gap-x-4 gap-y-6">
        <div class="text-left">
          <p class="text-md font-bold">Data da solicitação</p>
          <p class="mt-1 text-base">
            {{ formatDateToString(new Date(detailedException.requestDate)) }}
          </p>
        </div>
        <div class="text-left">
          <p class="text-md font-bold">ID da solicitação</p>
          <p class="mt-1 text-base">#{{ detailedException.id }}</p>
        </div>
        <div class="text-left">
          <p class="text-md font-bold">Status</p>
          <StatusBadge :status="detailedException.status" class="mt-1" />
        </div>
        <div class="text-left">
          <p class="text-md font-bold">Solicitante</p>
          <p class="mt-1 text-base">{{ detailedException.requester.name }}</p>
        </div>
        <div v-if="detailedException.evaluator" class="text-left">
          <p class="text-md font-bold">Aprovador</p>
          <p class="mt-1 text-base">{{ detailedException.evaluator.name }}</p>
        </div>
        <div v-if="detailedException.evaluationDate" class="text-left">
          <p class="text-md font-bold">Data da Aprovação</p>
          <p class="mt-1 text-base">
            {{ formatDateToString(new Date(detailedException.evaluationDate)) }}
          </p>
        </div>
      </div>

      <div class="text-left">
        <p class="text-md font-bold">Justificativa do Solicitante</p>
        <p class="mt-1 text-base leading-relaxed">
          {{ detailedException.justification || 'Nenhuma justificativa fornecida.' }}
        </p>
      </div>
    </div>

    <div v-if="detailedException" class="flex flex-col gap-y-4 mt-4 pb-4">
      <DropdownCard
        v-for="item in evaluationItems"
        :key="item.id"
        :title="item.title"
        :icon="
          item.approvalStatus === 'rejected' || item.approvalStatus === null ? mdiLock : item.icon
        "
        :approval-status="item.approvalStatus"
        :start-open="true"
      >
        <div
          class="text-sm flex flex-col gap-y-1 text-textPrimary text-left"
          v-html="item.details"
        ></div>
        <ApprovalChoice v-model="item.approvalStatus" />
        <TextareaInput
          v-model="item.analystJustification"
          placeholder="Digite sua justificativa..."
          :rows="4"
        />
      </DropdownCard>
    </div>

    <template #footer>
      <div v-if="isAwaitingApproval" class="flex flex-col w-full">
        <div
          v-if="
            !canActOnApproval && detailedException?.status === ExceptionStatusEnum.AWAITING_APPROVAL
          "
          class="bg-warning/20 border border-warning rounded-lg p-3 text-sm text-yellow-800 mb-4"
          data-testid="no-available-roles-message"
        >
          Você já registrou sua decisão neste fluxo ou não possui permissão para os papéis
          pendentes.
        </div>

        <div
          v-if="availableRoles.length >= 2"
          class="flex flex-col gap-y-2 mb-4"
          data-testid="role-selector"
        >
          <p class="text-md font-bold text-left">Aprovar como</p>
          <div class="flex gap-x-2">
            <v-chip
              v-for="role in availableRoles"
              :key="role"
              :variant="selectedRole === role ? 'flat' : 'outlined'"
              :color="selectedRole === role ? 'primary' : undefined"
              :data-testid="`role-selector-chip-${role}`"
              @click="selectedRole = role"
            >
              {{ VALIDATION_ROLE_LABELS[role] }}
            </v-chip>
          </div>
        </div>

        <p
          v-if="canActOnApproval && !allAreApproved"
          class="text-center text-sm text-textSecondary my-4"
        >
          É necessário definir uma ação (Aprovar/Recusar) para todos os itens para prosseguir.
        </p>

        <!-- Trava do Pós-Plantão (RN-07 / RF-12): aprovar dá à exceção
             precedência sobre a política de pagamento e muda o valor de um
             plantão já aprovado; recusar também é decisão registrada. Nos dois
             casos o ajuste passa antes pela reversão da validação. -->
        <div
          v-if="canActOnApproval && postStationLocked"
          class="flex items-start gap-2 px-3 py-2 my-4 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary text-left"
          data-testid="request-exception-post-station-lock-banner"
        >
          <SVGIcon :path="mdiLock" class="w-4 h-4 mt-0.5 shrink-0 text-primary30" />
          <span>{{ POST_STATION_LOCK_BANNER_TEXT }}</span>
        </div>

        <div v-if="canActOnApproval" class="flex justify-around gap-4 mt-2">
          <FilledButton
            v-if="canReproveException"
            text="Recusar pedido"
            width="46%"
            height="48px"
            class="!bg-primary30"
            :loading="isSubmitting"
            :disabled="
              isSubmitting || postStationLocked || (availableRoles.length >= 2 && !effectiveRole)
            "
            data-testid="reject-exception-button"
            @click="openConfirmation(ModalActionType.REJECT)"
          />

          <OutlinedButton
            v-if="canApproveException"
            text="Aprovar pedido"
            width="46%"
            height="48px"
            class="flex-grow !border-negative !text-negative !bg-white"
            :loading="isSubmitting"
            :disabled="
              isSubmitting ||
              postStationLocked ||
              !allAreApproved ||
              (availableRoles.length >= 2 && !effectiveRole)
            "
            data-testid="approve-exception-button"
            @click="openConfirmation(ModalActionType.APPROVE)"
          />
        </div>
      </div>
    </template>
  </BaseSidebar>

  <ExceptionConfirmationSidebar
    v-if="detailedException"
    :is-open="isConfirmModalOpen"
    :exception="detailedException"
    :loading="isSubmitting"
    :action-type="currentConfirmActionType"
    :approved-exceptions="approvedExceptionTitles"
    :rejected-exceptions="rejectedExceptionTitles"
    @update:is-open="isConfirmModalOpen = $event"
    @confirm-action="handleConfirmAction"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import { useToast } from 'vue-toastification'
import DOMPurify from 'dompurify'
import { getApiErrorMessage } from '@/utils/apiError'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiLock, mdiLockOpenVariant } from '@mdi/js'

import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import TextareaInput from '../Textarea/TextareaInput.vue'
import StatusBadge from '../Badges/StatusBadge.vue'

import DropdownCard from '../Cards/DropdownCard.vue'
import ApprovalChoice from '../Cards/ApprovalChoice.vue'
import ExceptionConfirmationSidebar from './ExceptionConfirmationSidebar.vue'
import StationContextCard from '../Cards/StationContextCard.vue'
import ExceptionalityProgressTimeline from '@/modules/company/Approvals/AuditoriumExceptionalities/components/ExceptionalityProgressTimeline.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { POST_STATION_LOCK_BANNER_TEXT } from '@/modules/company/PostStation/constants'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'
import { mapApprovalToTimeline } from '@/utils/approval-timeline-mapper'

import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'

import ExceptionService from '@/Network/Services/DoctorStationException'
import {
  ExceptionStatusEnum,
  type ExceptionSummary,
  ModalActionType
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'

import { formatDateToString, formatDateTimeWithTimezone } from '@/utils/date'
import { formatCurrency, formatProportionalityRule, formatRepassMode } from '@/utils/formatters'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { ValidationRole, VALIDATION_ROLE_LABELS } from '@/types/approval-flow.types'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

type ApprovalStatusOption = 'approved' | 'rejected' | null
type ApprovalStatus = 'Approved' | 'Rejected' | null

interface EvaluationItem {
  id: string
  title: string
  icon: string
  details: string
  approvalStatus: ApprovalStatusOption
  analystJustification: string
}

export interface ApprovalStatusMap {
  financials: ApprovalStatus
  repassMode: ApprovalStatus
  proportionalityRule: ApprovalStatus
  workJourney: ApprovalStatus
}

interface StationContextData {
  doctorName: string
  doctorCrm?: string
  scheduleName: string
  positionName: string
  shiftName?: string
  team?: string
  timeRange: string
  dutyType?: string
  value?: string
  formation?: string
  repassMode?: string
  proportionalityRule?: string
  breadcrumb?: string
  allocatedBy?: string
  startDetail?: string
  endDetail?: string
  timezone?: string
}

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    exceptionId: number | null
    scheduleId: number
    stationContext?: StationContextData
    /**
     * Trava do Pós-Plantão (RN-07 / RF-12) do plantão do pedido.
     *
     * Chega por prop porque este componente é do design system: sondar a
     * avaliação aqui (`Network.postStation`) violaria a camada. Quem calcula é
     * o módulo que abre a sidebar — `stationDetailsModal` ou as telas de
     * auditório, que têm o `stationId` em mão.
     */
    postStationLocked?: boolean
  }>(),
  { postStationLocked: false }
)

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'submitSuccess'): void
  (e: 'status-map-changed', statusMap: ApprovalStatusMap): void
}>()

const toast = useToast()
const { notifyError } = useErrorToast()
const exceptionService = new ExceptionService()
const permissionsStore = usePermissionsStore()
const { flags } = useFeatureFlags()

const detailedException = ref<ExceptionSummary | null>(null)
const loading = ref(false)
const isSubmitting = ref(false)
const isConfirmModalOpen = ref(false)
const currentConfirmActionType = ref<ModalActionType>(ModalActionType.REJECT)
const evaluationItems = ref<EvaluationItem[]>([])

const resetState = () => {
  detailedException.value = null
  evaluationItems.value = []
  loading.value = false
  isSubmitting.value = false
  isConfirmModalOpen.value = false
  selectedRole.value = null
}

const isSidebarOpen = computed({
  get: () => props.isOpen,
  set: (value) => emit('update:isOpen', value)
})

const allAreApproved = computed(() => {
  if (evaluationItems.value.length === 0) return false
  return evaluationItems.value.every((item) => item.approvalStatus === 'approved')
})

const isAwaitingApproval = computed(
  () => detailedException.value?.status === ExceptionStatusEnum.AWAITING_APPROVAL
)

const approvedApproversText = computed(() => {
  const decisions = detailedException.value?.approvalInstance?.decisions
  if (!decisions) return ''
  return decisions
    .filter((d) => d.decision === 'APPROVED' && d.userName)
    .map((d) => d.userName)
    .join(', ')
})

const pendingApproversText = computed(() => {
  const instance = detailedException.value?.approvalInstance
  if (!instance) return ''

  const pendingDecisions = instance.decisions.filter((d) => d.decision === 'PENDING')
  if (pendingDecisions.length === 0) return ''

  const operatorByOrder = new Map(instance.layers.map((l) => [l.order, l.operator]))

  const byLayer = new Map<number, string[]>()
  for (const d of pendingDecisions) {
    const label = VALIDATION_ROLE_LABELS[d.role as ValidationRole] ?? d.role
    if (!byLayer.has(d.layerOrder)) byLayer.set(d.layerOrder, [])
    byLayer.get(d.layerOrder)!.push(label)
  }

  const layerTexts: string[] = []
  for (const [order, labels] of [...byLayer.entries()].sort((a, b) => a[0] - b[0])) {
    const separator = operatorByOrder.get(order) === 'OR' ? ' ou ' : ' e '
    layerTexts.push(labels.join(separator))
  }

  return layerTexts.join(' e ')
})

const timelineProps = computed(() => {
  const instance = detailedException.value?.approvalInstance
  if (!instance) return null
  return mapApprovalToTimeline({
    approvalInstance: instance,
    requestDate: detailedException.value!.requestDate,
    requesterName: detailedException.value!.requester.name
  })
})

const availableRoles = computed<ValidationRole[]>(
  () => detailedException.value?.availableRolesForCurrentUser ?? []
)

const selectedRole = ref<ValidationRole | null>(null)

const effectiveRole = computed<ValidationRole | undefined>(() => {
  if (availableRoles.value.length === 1) return availableRoles.value[0]
  return selectedRole.value ?? undefined
})

const canActOnApproval = computed(() => {
  if (!flags.enabledApprovalEngine || !detailedException.value?.approvalInstance) return true
  return availableRoles.value.length > 0
})

const canReproveException = computed(() => {
  if (flags.enabledApprovalEngine && detailedException.value?.approvalInstance) {
    return canActOnApproval.value
  }
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationExceptionalitiesReproving,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const canApproveException = computed(() => {
  if (flags.enabledApprovalEngine && detailedException.value?.approvalInstance) {
    return canActOnApproval.value
  }
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationExceptionalitiesApproving,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const approvedExceptionTitles = computed(() =>
  evaluationItems.value
    .filter((item) => item.approvalStatus === 'approved')
    .map((item) => item.title)
)

const rejectedExceptionTitles = computed(() =>
  evaluationItems.value
    .filter((item) => item.approvalStatus === 'rejected')
    .map((item) => item.title)
)

const formatHours = (decimalHours: string | number | null | undefined): string => {
  const num = Number(decimalHours)
  if (isNaN(num) || num < 0) {
    return '00:00'
  }

  const hours = Math.floor(num)
  const minutesFraction = num - hours
  const minutes = Math.round(minutesFraction * 60)

  const paddedHours = String(hours).padStart(2, '0')
  const paddedMinutes = String(minutes).padStart(2, '0')

  return `${paddedHours}:${paddedMinutes}`
}

watch(
  evaluationItems,
  (newItems) => {
    const statusMap: ApprovalStatusMap = {
      financials: null,
      repassMode: null,
      proportionalityRule: null,
      workJourney: null
    }

    newItems.forEach((item) => {
      if (item.id in statusMap) {
        const key = item.id as keyof ApprovalStatusMap
        if (item.approvalStatus === 'approved') {
          statusMap[key] = 'Approved'
        } else if (item.approvalStatus === 'rejected') {
          statusMap[key] = 'Rejected'
        } else {
          statusMap[key] = null
        }
      }
    })

    emit('status-map-changed', statusMap)
  },
  { deep: true }
)

watchEffect(async () => {
  if (!isSidebarOpen.value || !props.exceptionId) {
    resetState()
    return
  }

  if (detailedException.value?.id === props.exceptionId) {
    return
  }

  loading.value = true
  try {
    detailedException.value = await exceptionService.getDetails(props.exceptionId, props.scheduleId)
  } catch (error) {
    console.error('Failed to fetch exception details:', error)
    // Antes: literal fixo, descartando a mensagem do backend (404 do pedido, etc.).
    notifyError(error, 'Não foi possível carregar os detalhes da excepcionalidade.')
    detailedException.value = null
  } finally {
    loading.value = false
  }
})

watch(detailedException, (newException) => {
  if (!newException) {
    evaluationItems.value = []
    return
  }

  const e = newException

  const getWorkJourneyDetails = () => `
    <p><strong>Duração base:</strong> ${formatHours(e.financials.baseDurationHours)} horas</p>
    <p><strong>Duração excepcional:</strong> ${formatHours(
      e.financials.exceptionalDurationHours
    )} horas</p>
    <p><strong>Horas perdidas:</strong> ${formatHours(e.financials.lostHours)} horas</p>
    <p><strong>Valor de horas perdidas:</strong> ${formatCurrency(
      Number(e.financials.valueOfLostHours)
    )}</p>
  `

  evaluationItems.value = [
    {
      id: 'financials',
      title: 'Valor excepcional',
      icon: mdiLockOpenVariant,
      details: DOMPurify.sanitize(`
        <p><strong>Valor base:</strong> ${formatCurrency(
          Math.round(Number(e.financials.baseCost) * Number(e.financials.baseDurationHours))
        )}</p>
        <p><strong>Valor excepcional:</strong> ${formatCurrency(
          Number(e.financials.totalExceptionalValue)
        )}</p>
        <p><strong>Prejuízo previsto:</strong> <span class="text-red-600 font-semibold">${formatCurrency(Math.floor(Number(e.financials.estimatedLossTotal)))}</span></p>
      `),
      approvalStatus: null,
      analystJustification: ''
    },
    {
      id: 'repassMode',
      title: 'Modalidade de repasse',
      icon: mdiLockOpenVariant,
      details: DOMPurify.sanitize(
        `<p><strong>Original:</strong> ${formatRepassMode(
          e.changes.repassMode?.original
        )}</p><p><strong>Solicitado:</strong> ${formatRepassMode(e.changes.repassMode?.requested)}</p>`
      ),
      approvalStatus: null,
      analystJustification: ''
    },
    {
      id: 'proportionalityRule',
      title: 'Regra de proporcionalidade',
      icon: mdiLockOpenVariant,
      details: DOMPurify.sanitize(
        `<p><strong>Original:</strong> ${formatProportionalityRule(
          e.changes.proportionalityRule?.original
        )}</p><p><strong>Solicitado:</strong> ${formatProportionalityRule(
          e.changes.proportionalityRule?.requested
        )}</p>`
      ),
      approvalStatus: null,
      analystJustification: ''
    },
    {
      id: 'workJourney',
      title: 'Jornada de trabalho',
      icon: mdiLockOpenVariant,
      details: DOMPurify.sanitize(getWorkJourneyDetails()),
      approvalStatus: null,
      analystJustification: ''
    }
  ]
})

function openConfirmation(actionType: ModalActionType) {
  currentConfirmActionType.value = actionType
  isConfirmModalOpen.value = true
}

async function handleConfirmAction(action: ModalActionType) {
  isConfirmModalOpen.value = false
  await handleSubmit(action)
}

async function handleSubmit(action: ModalActionType) {
  if (!detailedException.value) {
    toast.error('Detalhes do pedido não disponíveis para submissão.')
    return
  }

  isSubmitting.value = true
  try {
    const isApproval = action === ModalActionType.APPROVE
    const targetStatus = isApproval ? 'approved' : 'rejected'
    const approverRole = detailedException.value.approvalInstance ? effectiveRole.value : undefined

    const justification = evaluationItems.value
      .filter((item) => item.approvalStatus === targetStatus && item.analystJustification)
      .map((item) => `${item.title}: ${item.analystJustification}`)
      .join('; ')

    const response = isApproval
      ? await exceptionService.approve(
          detailedException.value.id,
          justification,
          props.scheduleId,
          approverRole
        )
      : await exceptionService.reject(
          detailedException.value.id,
          justification,
          props.scheduleId,
          approverRole
        )

    const isStillInProgress = response.status === 'PENDING'
    if (isStillInProgress) {
      toast.success('Sua decisão foi registrada. Aguardando demais validadores.')
    } else {
      toast.success('Solicitação processada com sucesso!')
    }

    emit('submitSuccess')
    isSidebarOpen.value = false
  } catch (error: any) {
    console.error('[RequestExceptionalitySidebar] Submission error:', error)

    // Trava do Pós-Plantão: garante a frase certa mesmo quando o backend não
    // manda `message`, e mantém a sidebar aberta (o `isSidebarOpen = false` só
    // roda na trilha de sucesso).
    if (isPostStationApprovedError(error)) {
      toast.error(getPostStationApprovedMessage(error))
      return
    }

    // getApiErrorMessage cobre o ServiceError do service (que embrulha o erro
    // HTTP) e o shape axios cru — a extração manual de response.data.message
    // deixaria de funcionar com o erro normalizado.
    toast.error(getApiErrorMessage(error, 'Falha ao processar a solicitação.'))
  } finally {
    isSubmitting.value = false
  }
}
</script>
